import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { promoCodeAPI } from '../../services/api';
import CreatePromoCodeDialog from '../../components/dialogs/CreatePromoCodeDialog';
import EditPromoCodeDialog from '../../components/dialogs/EditPromoCodeDialog';
import PromoCodeUsageDialog from '../../components/dialogs/PromoCodeUsageDialog';
import Breadcrumbs from '../../components/Breadcrumbs';

const PromoCodeManagement = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [organization, setOrganization] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [search, setSearch] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [selectedPromoCode, setSelectedPromoCode] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);
  
  // Statistics
  const [stats, setStats] = useState({
    totalActive: 0,
    totalUses: 0,
    totalDiscountGiven: 0
  });
  
  useEffect(() => {
    fetchPromoCodes();
  }, [organization, status, search, page]);
  
  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await promoCodeAPI.getAllPromoCodes({
        page,
        limit,
        organization,
        status,
        search
      });
      
      // Handle both response.success and direct data formats
      if (response && (response.success || response.data)) {
        const promoCodesData = response.data || response;
        const paginationData = response.pagination || { total: promoCodesData.length || 0 };
        
        // Ensure data is an array
        const codesArray = Array.isArray(promoCodesData) ? promoCodesData : [];
        
        setPromoCodes(codesArray);
        setTotal(paginationData.total || codesArray.length);
        
        // Calculate stats
        const activeCount = codesArray.filter(p => p.status === 'ACTIVE').length;
        const totalUses = codesArray.reduce((sum, p) => sum + (p.currentUsageCount || 0), 0);
        
        setStats({
          totalActive: activeCount,
          totalUses: totalUses,
          totalDiscountGiven: 0 // This would need a separate API call
        });
      } else {
        setError(response?.error || 'Failed to fetch promo codes');
        setPromoCodes([]);
        setTotal(0);
      }
    } catch (err) {
      console.error('Error fetching promo codes:', err);
      setError(err.message || 'Failed to fetch promo codes');
      setPromoCodes([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreate = async (data) => {
    try {
      const response = await promoCodeAPI.createPromoCode(data);
      
      if (response.success) {
        setSuccess('Promo code created successfully');
        setCreateDialogOpen(false);
        fetchPromoCodes();
      } else {
        throw new Error(response.error || 'Failed to create promo code');
      }
    } catch (err) {
      console.error('Error creating promo code:', err);
      throw err;
    }
  };
  
  const handleEdit = async (id, data) => {
    try {
      const response = await promoCodeAPI.updatePromoCode(id, data);
      
      if (response.success) {
        setSuccess('Promo code updated successfully');
        setEditDialogOpen(false);
        fetchPromoCodes();
      } else {
        throw new Error(response.error || 'Failed to update promo code');
      }
    } catch (err) {
      console.error('Error updating promo code:', err);
      throw err;
    }
  };
  
  const handleDelete = async () => {
    if (!promoToDelete) return;
    
    try {
      const response = await promoCodeAPI.deletePromoCode(promoToDelete.id);
      
      if (response.success) {
        setSuccess('Promo code deactivated successfully');
        setDeleteDialogOpen(false);
        setPromoToDelete(null);
        fetchPromoCodes();
      } else {
        setError(response.error || 'Failed to deactivate promo code');
      }
    } catch (err) {
      console.error('Error deleting promo code:', err);
      setError('Failed to deactivate promo code');
    }
  };
  
  const handleViewUsage = (promoCode) => {
    setSelectedPromoCode(promoCode);
    setUsageDialogOpen(true);
  };
  
  const getPromoTypeLabel = (type) => {
    const labels = {
      'PERCENTAGE_DISCOUNT': 'Percentage Off',
      'FIXED_PRICE': 'Fixed Price',
      'BUY_X_GET_Y_FREE': 'Buy X Get Y Free',
      'BUY_X_GET_Y_DISCOUNTED': 'Buy X Get Y Discounted'
    };
    return labels[type] || type;
  };
  
  const getDiscountDisplay = (promoCode) => {
    switch (promoCode.promoType) {
      case 'PERCENTAGE_DISCOUNT':
        return `${promoCode.percentDiscount}% off`;
      case 'FIXED_PRICE':
        return `$${Number(promoCode.fixedPricePerUnit).toFixed(2)}/unit`;
      case 'BUY_X_GET_Y_FREE':
        return `Buy ${promoCode.buyQuantity} Get ${promoCode.getQuantity} Free`;
      case 'BUY_X_GET_Y_DISCOUNTED':
        return `Buy ${promoCode.buyQuantity} Get ${promoCode.getQuantity} at ${promoCode.getDiscountPercent}% off`;
      default:
        return '-';
    }
  };
  
  return (
    <Box>
      <Breadcrumbs />
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              Promo Code Management
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
              Create and manage promotional codes and discounts
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              bgcolor: 'white',
              color: '#039582',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.9)',
                color: '#027A6B'
              }
            }}
          >
            Create New Promo Code
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ px: { xs: 2, md: 3 } }}>
        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        
        {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {stats.totalActive}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Promo Codes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                {stats.totalUses}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Uses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'info.main' }}>
                ${stats.totalDiscountGiven.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Discount Given
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search Code or Description"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  endAdornment: <SearchIcon />
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={organization}
                  label="Organization"
                  onChange={(e) => setOrganization(e.target.value)}
                >
                  <MenuItem value="ALL">All Organizations</MenuItem>
                  <MenuItem value="BARE">BARE</MenuItem>
                  <MenuItem value="EPL">EPL</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <MenuItem value="ALL">All Statuses</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                  <MenuItem value="EXPIRED">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={fetchPromoCodes}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Organization</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Discount</strong></TableCell>
                <TableCell align="center"><strong>Uses</strong></TableCell>
                <TableCell align="center"><strong>Limit</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : promoCodes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No promo codes found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                promoCodes.map((promo) => (
                  <TableRow key={promo.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
                        {promo.code}
                      </Typography>
                      {promo.description && (
                        <Typography variant="caption" color="text.secondary">
                          {promo.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={promo.organization}
                        size="small"
                        color={promo.organization === 'BARE' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getPromoTypeLabel(promo.promoType)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getDiscountDisplay(promo)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {promo.currentUsageCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {promo.usageLimitTotal || '∞'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={promo.status}
                        size="small"
                        color={
                          promo.status === 'ACTIVE' ? 'success' :
                          promo.status === 'EXPIRED' ? 'error' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Usage">
                        <IconButton
                          size="small"
                          onClick={() => handleViewUsage(promo)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedPromoCode(promo);
                            setEditDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Deactivate">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setPromoToDelete(promo);
                            setDeleteDialogOpen(true);
                          }}
                          disabled={promo.status !== 'ACTIVE'}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {total > limit && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Typography sx={{ mx: 2, alignSelf: 'center' }}>
              Page {page} of {Math.ceil(total / limit)}
            </Typography>
            <Button
              disabled={page >= Math.ceil(total / limit)}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </Card>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate the promo code <strong>{promoToDelete?.code}</strong>?
            This will prevent customers from using it, but usage history will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
      
      {/* Dialogs */}
      <CreatePromoCodeDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreate}
      />
      
      {selectedPromoCode && (
        <>
          <EditPromoCodeDialog
            open={editDialogOpen}
            onClose={() => {
              setEditDialogOpen(false);
              setSelectedPromoCode(null);
            }}
            promoCode={selectedPromoCode}
            onUpdate={handleEdit}
          />
          
          <PromoCodeUsageDialog
            open={usageDialogOpen}
            onClose={() => {
              setUsageDialogOpen(false);
              setSelectedPromoCode(null);
            }}
            promoCode={selectedPromoCode}
          />
        </>
      )}
    </Box>
  );
};

export default PromoCodeManagement;

